import { Button, pantryColors, pantryTypography } from '@pantry/ui'
import React from 'react'
import { createPortal } from 'react-dom'

const p = pantryColors
const F = pantryTypography.fontFamily

interface SyncPosDataDialogProps {
  open: boolean
  isSaving: boolean
  onClose: () => void
  onConfirm: () => void
}

export const SyncPosDataDialog: React.FC<SyncPosDataDialogProps> = ({
  open,
  isSaving,
  onClose,
  onConfirm,
}) => {
  if (!open) return null

  // Portal to document.body so the overlay escapes any ancestor stacking
  // context — PosDataCard sits inside the editor's sticky sidebar column,
  // which (like position: fixed) always creates its own stacking context.
  // Without a portal, a non-portaled fixed-position child stays trapped
  // beneath the page header regardless of its own zIndex.
  return createPortal(
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={iconWrapStyle}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ width: 14, height: 14 }}
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
          </div>
          <h3 style={titleStyle}>Sync data from POS?</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={closeBtnStyle}
          >
            ×
          </button>
        </div>

        <div style={bodyStyle}>
          <p style={promptStyle}>
            Syncing will replace this product&apos;s details with the latest
            data from your POS. Any changes you made manually will be
            overwritten. Do you want to continue?
          </p>
        </div>

        <div style={footerStyle}>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isSaving}>
            {isSaving ? 'Syncing…' : 'Sync now'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 300,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
}

const modalStyle: React.CSSProperties = {
  background: p.white,
  borderRadius: 12,
  width: 400,
  maxWidth: 'calc(100vw - 32px)',
  boxShadow: '0 20px 60px rgba(0,0,0,.25)',
  overflow: 'hidden',
  fontFamily: F,
}

const headerStyle: React.CSSProperties = {
  padding: '14px 18px',
  borderBottom: `1px solid ${p.gray[200]}`,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}

const iconWrapStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  background: p.amber[50],
  color: p.amber[700],
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
}

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: p.gray[800],
  flex: 1,
  margin: 0,
}

const closeBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: p.gray[400],
  fontSize: 18,
  lineHeight: 1,
  padding: '2px 6px',
  fontFamily: F,
}

const bodyStyle: React.CSSProperties = {
  padding: '16px 18px',
}

const promptStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: p.gray[700],
  lineHeight: 1.5,
}

const footerStyle: React.CSSProperties = {
  padding: '12px 18px',
  borderTop: `1px solid ${p.gray[200]}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
}
